import { type LogEvent } from '@unisporkal/instrumentation';
import {
  createRouteContext,
  type UnisporkalMiddleware,
} from '@unisporkal/federation';
import {
  AgreementsFactory,
  SearchFactory,
  SearchParametersFactory,
  SearchPreferencesFactory,
  type SearchParameters,
} from 'app/domain';
import { getSiteConfig } from 'app/react/config/site';
import { SearchPreferenceCookie } from 'app/react/shared/api/searchPreferenceCookie';
import {
  agreementsQuery,
  createApi,
  searchProfileQuery,
} from 'app/react/shared/api';
import { parametersFromRoute } from '../parametersFromRoute';
import { searchResolutionDrift } from './searchResolutionDrift';

/**
 * The search this entry resolved to, or `null` when it could not be resolved.
 *
 * Written here for the loaders in the same request. **Nothing reads it yet** —
 * the consumer arrives with the search-results path, which does not exist in
 * this stack: results still come off the Rails-rendered payload.
 *
 * It is not serialized, and will not need to be. Federation drops document
 * middleware from the client route tree, so this never runs there; what makes
 * that survivable is that the client will hold the same url, the same cookie
 * and the same dehydrated profile, and can resolve from them rather than be
 * handed an answer. That client-side resolution is not written yet either.
 *
 * `null` rather than no default, because reading a key nothing has set throws
 * — which would turn a failure to resolve into a failed page, exactly the
 * thing the entry is written to survive. A reader that finds `null` resolves
 * the search itself, which it can already do.
 */
export const resolvedSearch = createRouteContext<SearchParameters | null>(null);

const parameters = new SearchParametersFactory();
const preferences = new SearchPreferencesFactory();
const agreements = new AgreementsFactory();

/**
 * Records which parameters the two resolutions disagree about.
 *
 * Structured rather than a message, so the tracker groups every drift together
 * and the rate can be alerted on; a value in the message would make one group
 * per search.
 *
 * **Names, not values, and the path without its query.** `phrase` is among the
 * parameters compared and is whatever the visitor typed, so neither it nor the
 * query string belongs in a tracker. The schema is the authority on which
 * parameters are free text and this module cannot see it from out here, so
 * values are left out rather than filtered — the path and the parameter names
 * are enough to reproduce.
 */
const reportDrift = (
  logEvent: LogEvent | undefined,
  pathname: string,
  drift: ReturnType<typeof searchResolutionDrift>
): void => {
  if (!drift.length) return;

  logEvent?.addContext('searchResolutionDrift', {
    path: pathname,
    parameters: drift.map(({ parameter }) => parameter),
  });
};

type ResolveArgs = Parameters<UnisporkalMiddleware>[0];

/**
 * Resolves the search, or answers `null` when it cannot.
 *
 * A failure leaves the search unresolved rather than the page unrendered. The
 * client holds the same url, the same cookie and the same dehydrated profile,
 * so it can reach this answer itself; failing the document would spend the
 * page on something the page can still do.
 */
const resolve = async (
  { cookies, url, params, axios, fetchQuery, site }: ResolveArgs,
  logEvent?: LogEvent
): Promise<SearchParameters | null> => {
  try {
    const { agreementsService, searchProfileService } = createApi(axios);

    const [profile, agreementsResponse] = await Promise.all([
      fetchQuery(searchProfileQuery(searchProfileService)),
      fetchQuery(agreementsQuery(agreementsService)),
    ]);

    const { searchParameters: siteExclusions } = getSiteConfig(site.nickname);

    // The url is its path and its query together. `params` carries both what
    // the route matched and what it declared its path states —
    // `/search/2/film` is a film search though no segment or query parameter
    // spells it. What the route cannot declare it derives — a gateway
    // category names its phrase in a dynamic segment. Both come after the
    // query, so a url saying film in its path is not talked out of it by a
    // stray query parameter.
    const stated = parameters.fromQuery({
      ...Object.fromEntries(url.searchParams),
      ...params,
      ...parametersFromRoute(params),
    });

    // Still the parameters, not the `Search`: the drift check is the only
    // reader, and the route context carries what it compares. The search
    // itself goes in once something needs more than its parameters.
    return new SearchFactory({
      // Federation supplies the request's cookie store, so the same `find`
      // works on either side and nothing here reads a header.
      preferences: preferences.fromDTO(SearchPreferenceCookie.find(cookies), {
        excludeNudity: profile.excludeNudity,
      }),
      sortProfile: { botOrFirstTimeVisitor: profile.botOrFirstTimeVisitor },
      siteExclusions,
    }).enter(stated, agreements.fromDTO(agreementsResponse?.agreements ?? {}))
      .parameters;
  } catch (error) {
    // Federation short-circuits by throwing a Response — `notFound()`,
    // `redirectTo()`, `signInRedirect()` — so one of those is the chain's
    // business rather than a failure to resolve.
    if (typeof Response !== 'undefined' && error instanceof Response) {
      throw error;
    }

    logEvent?.handledError(
      'provideResolvedSearch',
      error instanceof Error ? error : new Error(String(error))
    );

    return null;
  }
};

/**
 * Resolves the search a url asks for, once per document request.
 *
 * **Document middleware, not middleware.** Preferences are applied at two
 * moments and nowhere else — arriving, and crossing into a different scope.
 * Declared as plain `middleware` this would also run on every paginate, filter
 * change and back-navigation, re-asserting a stored choice over one the
 * visitor had just made in between. Federation drops document middleware from
 * the client route tree, so that is structural rather than a convention to
 * remember.
 *
 * The scope-change half belongs to whatever commits a search, not here.
 */
const provideResolvedSearch =
  (
    logEvent?: LogEvent,
    /**
     * The search the rendered page actually ran, from the payload. Supplied
     * only so the resolution can be checked against it — see
     * `searchResolutionDrift`. Nothing resolves differently because of it.
     */
    searched?: Readonly<Record<string, unknown>>
  ): UnisporkalMiddleware =>
  async (args, next) => {
    const resolution = await resolve(args, logEvent);
    if (resolution) args.context.set(resolvedSearch, resolution);

    // Its own try: a failure while *checking* the resolution is not a failure
    // to resolve one, and reporting it as such would claim the entry broke
    // when the search it produced was fine.
    if (resolution) {
      try {
        reportDrift(
          logEvent,
          args.url.pathname,
          searchResolutionDrift(resolution, searched)
        );
      } catch (error) {
        logEvent?.handledError(
          'searchResolutionDrift',
          error instanceof Error ? error : new Error(String(error))
        );
      }
    }

    // Outside both: this middleware answers for its own resolution, not for
    // the loaders and middleware beneath it. An error raised down there is
    // theirs to surface.
    await next();
  };

export default provideResolvedSearch;
